import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuranAudio } from "../../contexts/QuranAudioContext";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Play, Pause, SkipBack, SkipForward, Repeat, X, Loader2 } from "lucide-react";
import { useLanguage } from "../../App";

const GlobalQuranAudioPlayer = () => {
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const navigate = useNavigate();
  const {
    currentSurah,
    currentAyah,
    isPlaying,
    audioProgress,
    repeatMode,
    reciter,
    loopEnabled,
    loopCount,
    loopRemaining,
    probingReciter,
    visible,
    RECITERS,
    LOOP_COUNT_OPTIONS,
    togglePlay,
    playNext,
    playPrev,
    stopAudio,
    setRepeatMode,
    setReciter,
    setLoopEnabled,
    setLoopCount,
  } = useQuranAudio();

  if (!visible || !currentSurah) return null;

  const handleNavigateToQuran = () => {
    if (currentSurah) {
      navigate(`/quran/${currentSurah.number}?ayah=${currentAyah}`);
    }
  };

  return (
    <div className="global-audio-player fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-t shadow-2xl safe-bottom">
      <div className="max-w-3xl mx-auto px-2 py-2 md:px-4 md:py-3">
        {/* Progress bar */}
        <div className="w-full h-1 bg-muted rounded-full mb-2 overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${audioProgress}%` }}
          />
        </div>

        <div className="flex items-center gap-1 md:gap-3">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full h-7 w-7 md:h-8 md:w-8 flex-shrink-0 text-muted-foreground hover:text-foreground"
            onClick={stopAudio}
            title={isEnglish ? "Close" : "Tutup"}
          >
            <X className="w-3 h-3 md:w-4 md:h-4" />
          </Button>

          {/* Play controls */}
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full h-8 w-8 md:h-9 md:w-9 flex-shrink-0"
            onClick={playPrev}
            disabled={currentAyah <= 1 || probingReciter}
            title={isEnglish ? "Previous" : "Sebelumnya"}
          >
            <SkipBack className="w-4 h-4" />
          </Button>

          <Button
            size="icon"
            className="rounded-full h-9 w-9 md:h-10 md:w-10 flex-shrink-0"
            onClick={togglePlay}
            disabled={probingReciter && !isPlaying}
          >
            {probingReciter && !isPlaying ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 ml-0.5" />
            )}
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="rounded-full h-8 w-8 md:h-9 md:w-9 flex-shrink-0"
            onClick={playNext}
            disabled={currentAyah >= (currentSurah?.ayahs?.length || 0) || probingReciter}
            title={isEnglish ? "Next" : "Selanjutnya"}
          >
            <SkipForward className="w-4 h-4" />
          </Button>

          {/* Surah info - clickable to navigate */}
          <button
            onClick={handleNavigateToQuran}
            className="flex-1 min-w-0 px-2 text-left hover:bg-muted/50 rounded-lg transition-colors py-1"
          >
            <p className="font-medium truncate text-xs md:text-sm">
              {currentSurah?.englishName || (isEnglish ? "..." : "...")}
            </p>
            <p className="text-[10px] md:text-xs text-muted-foreground truncate">
              {isEnglish ? `v.${currentAyah}` : `Ayat ${currentAyah}`}
              {loopEnabled && loopRemaining > 0 ? ` · ${loopRemaining}` : ""}
            </p>
          </button>

          {/* Controls */}
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            {probingReciter && (
              <Loader2 className="w-3 h-3 text-muted-foreground animate-spin flex-shrink-0" />
            )}

            <Select value={reciter} onValueChange={setReciter} disabled={probingReciter}>
              <SelectTrigger className="h-7 w-12 md:w-20 text-xs px-2" title={isEnglish ? "Reciter" : "Qari"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECITERS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={repeatMode} onValueChange={setRepeatMode}>
              <SelectTrigger className="h-7 w-14 md:w-20 text-xs px-2" title={isEnglish ? "Repeat" : "Ulangi"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{isEnglish ? "Off" : "Mati"}</SelectItem>
                <SelectItem value="one">{isEnglish ? "Ayah" : "Ayat"}</SelectItem>
                <SelectItem value="all">{isEnglish ? "Surah" : "Surah"}</SelectItem>
              </SelectContent>
            </Select>

            <Button
              size="icon"
              variant={loopEnabled ? "default" : "outline"}
              onClick={() => setLoopEnabled(!loopEnabled)}
              className="h-7 w-7 flex-shrink-0"
              title={isEnglish ? "Loop ayah" : "Loop ayat"}
            >
              <Repeat className="w-3 h-3" />
            </Button>

            <Select
              value={String(loopCount)}
              onValueChange={(value) => setLoopCount(Number(value))}
              disabled={!loopEnabled}
            >
              <SelectTrigger className="h-7 w-10 text-xs px-1" title={isEnglish ? "Loop count" : "Jumlah loop"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOOP_COUNT_OPTIONS.map((count) => (
                  <SelectItem key={count} value={String(count)}>
                    {count}x
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalQuranAudioPlayer;
